import { capitalize } from "lodash";

import { colorsPalettes } from "@similarweb/styles";

import { chosenItems } from "app/common/services/chosenItems";
import { CHART_COLORS } from "app/constants/ChartColors";
import type {
    DataList,
    IOverTimeChartDataItem,
    OverTimeChartCategoryDataItem,
    ChartDataTypes,
} from "app/pages/app-intelligence/app-analysis/pages/Retention/types";

const CATEGORY_DATA_COLOR = colorsPalettes.carbon[200];

const getDataList = (rawDataList): DataList => {
    return Object.entries(rawDataList).map(([key, value]: [string, number]) => [parseInt(key.substring(1)), value]);
};

const getCategoryData = (rawCategoryData): OverTimeChartCategoryDataItem[] => {
    return Object.entries(rawCategoryData).map(([itemId, itemData]) => {
        if (!itemData) {
            return {
                categoryBenchmark: true,
                name: capitalize(itemId),
                id: itemId,
                color: CATEGORY_DATA_COLOR,
                lineWidth: 8,
                zIndex: 0,
                data: null,
                visible: true,
            };
        }
        return {
            categoryBenchmark: true,
            name: capitalize(itemId),
            id: itemId,
            color: CATEGORY_DATA_COLOR,
            lineWidth: 8,
            zIndex: 0,
            data: getDataList(itemData),
            visible: true,
        };
    });
};

const getChartData = (rawAppsRetentionData): { chartData: IOverTimeChartDataItem[]; mainItemCategory: string } => {
    const allChosenItems = chosenItems.$all();
    const itemColors = CHART_COLORS.chartMainColors;
    let mainItemCategory: string;
    const chartData: IOverTimeChartDataItem[] = Object.entries(rawAppsRetentionData).map(
        ([itemId, itemData]: [string, Record<string, any>], index) => {
            const chosenItem = allChosenItems.find((item) => item.Id === itemId);
            if (!itemData) {
                return {
                    name: chosenItem?.Title ?? itemId,
                    id: itemId,
                    color: chosenItem?.Color ?? itemColors[index],
                    data: null,
                    visible: true,
                    zIndex: 1,
                };
            }
            const { AppData: data, CategoryName: category } = itemData;
            // use the main item's category if it exists, and if it is undefined,
            // use then the first item with a category
            if (!mainItemCategory) {
                mainItemCategory = category;
            }
            return {
                name: chosenItem?.Title ?? itemId,
                id: itemId,
                color: chosenItem?.Color ?? itemColors[index],
                data: getDataList(data),
                visible: true,
                zIndex: 1,
            };
        },
    );

    return { chartData, mainItemCategory };
};

export const transformOverTimeData = (rawChartData): ChartDataTypes => {
    const rawCategoryData = rawChartData.CategoriesRetention;
    const { chartData, mainItemCategory: initialSelectedCategory } = getChartData(rawChartData.AppsRetention);

    const categoryData = getCategoryData(rawCategoryData);
    const categoriesList = Object.keys(rawCategoryData);

    chartData.push({
        categoryBenchmark: true,
        name: capitalize(initialSelectedCategory),
        id: initialSelectedCategory,
        color: CATEGORY_DATA_COLOR,
        lineWidth: 8,
        zIndex: 0,
        data: rawCategoryData[initialSelectedCategory] ? getDataList(rawCategoryData[initialSelectedCategory]) : null,
        visible: true,
    } as OverTimeChartCategoryDataItem);

    return { chartData, categoryData, categoriesList, initialSelectedCategory };
};
